/**
 * Internal helper only — never exported from the barrel.
 *
 * `tsconfig.json` sets `exactOptionalPropertyTypes`, which is stricter
 * than Material UI's own prop types: most of Material UI's optional props
 * are declared as `foo?: T`, not `foo?: T | undefined`, so explicitly
 * passing `foo={undefined}` (which happens constantly here, because this
 * adapter's own optional props are `T | undefined` once destructured)
 * fails the compiler even though the value is legitimately absent.
 *
 * This strips keys whose value is `undefined` before the object reaches
 * Material UI's props, so "not provided" is represented the same way
 * (key absent) on both sides of the boundary. It changes nothing about
 * which values are forwarded — `null` and `false` pass through unchanged.
 */
export function cleanProps<T extends Record<string, unknown>>(
  props: T,
): { [K in keyof T]?: Exclude<T[K], undefined> } {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result as { [K in keyof T]?: Exclude<T[K], undefined> };
}
